import { useEffect, useState } from "react";
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, Platform, Linking, ActivityIndicator, Image } from "react-native";
import { useRouter, useLocalSearchParams } from "expo-router";
import Head from "expo-router/head";
import { PublicNav } from "../../../components/PublicNav";

const NAVY = "#1B2A4A";
const NAVY_DARK = "#111C30";
const ORANGE = "#F5A623";
const WHITE = "#FFFFFF";
const TEXT = "#1F2937";
const API = "https://dashhound.app/api";

type Ev = {
  id: string; name: string; club: string; date: string; closingDate?: string; closingTime?: string;
  openingDate?: string; address?: string; city?: string; state?: string; zip?: string;
  entryUrl?: string; entryFee?: string; dayOfRunsFee?: string; eligibleBreeds?: string;
  eventSetting?: string; lureOperator?: string; eventChair?: string; eventChairContact?: string;
  secretary?: string; secretaryContact?: string; clubWebsite?: string; premiumListUrl?: string;
  eventWhatSetsApart?: string; clubLogoUrl?: string; clubRaffle?: string;
  eventPhotographer?: string; eventPhotographerUrl?: string;
  clubGallery?: any[]; clubRibbonPreviews?: any[]; clubRibbonPreviewUrl?: string;
};

const imgUrls = (arr?: any[]) => (arr || []).map((x) => (typeof x === "string" ? x : x?.url || x?.src)).filter(Boolean);

function fmtDate(d?: string) {
  if (!d) return "";
  const dt = new Date(String(d).replace(" ", "T"));
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });
}

export default function EventDetail() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const [ev, setEv] = useState<Ev | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetch(`${API}/events/${id}`)
      .then((r) => { if (!r.ok) throw new Error("nf"); return r.json(); })
      .then(setEv).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [id]);

  // Back to the events search — use browser back on web so the visitor's search
  // results are preserved instead of forcing them to search again.
  const backToEvents = () => {
    if (Platform.OS === "web" && typeof window !== "undefined" && window.history.length > 1) window.history.back();
    else router.push("/(auth)/fast-cat-events/" as any);
  };

  const title = ev ? `${ev.name || ev.club} — Fast CAT Event` : "Fast CAT Event";
  const loc = ev ? [ev.city, ev.state].filter(Boolean).join(", ") : "";
  const gallery = ev ? imgUrls(ev.clubGallery) : [];
  const ribbons = ev ? [...(ev.clubRibbonPreviewUrl ? [ev.clubRibbonPreviewUrl] : []), ...imgUrls(ev.clubRibbonPreviews)] : [];

  return (
    <View style={{ flex: 1, backgroundColor: WHITE }}>
      {Platform.OS === "web" && (
        <Head>
          <title>{`${title} — Dash-Hound`}</title>
          <meta name="description" content={ev ? `${ev.name || ev.club} — an AKC Fast CAT event${loc ? ` in ${loc}` : ""} on ${fmtDate(ev.date)}. See details and how to enter.` : "AKC Fast CAT event details."} />
        </Head>
      )}

      <PublicNav />

      <View style={styles.subBar}>
        <TouchableOpacity onPress={backToEvents}><Text style={styles.subBarBack}>← All Events</Text></TouchableOpacity>
      </View>

      {loading ? (
        <View style={styles.loading}><ActivityIndicator color={NAVY} /></View>
      ) : notFound || !ev ? (
        <View style={styles.loading}>
          <Text style={styles.hint}>We couldn't find that event.</Text>
          <TouchableOpacity style={[styles.enterBtn, { marginTop: 16, paddingHorizontal: 24 }]} onPress={backToEvents}><Text style={styles.enterBtnText}>Find Events</Text></TouchableOpacity>
        </View>
      ) : (
        <ScrollView contentContainerStyle={styles.content}>
          <View style={styles.hero}>
            {ev.clubLogoUrl ? (
              Platform.OS === "web"
                // @ts-ignore
                ? <img src={ev.clubLogoUrl} alt={ev.club} style={{ height: 64, objectFit: "contain", marginBottom: 14 } as any} />
                : <Image source={{ uri: ev.clubLogoUrl }} style={{ height: 64, width: 160, marginBottom: 14 }} resizeMode="contain" />
            ) : null}
            <Text style={styles.kicker}>FAST CAT EVENT</Text>
            <Text style={styles.title}>{ev.name || ev.club}</Text>
            {ev.name && ev.club && ev.name !== ev.club ? <Text style={styles.club}>{ev.club}</Text> : null}
          </View>

          <View style={styles.body}>
            {ev.entryUrl ? (
              <TouchableOpacity style={styles.enterBtn} onPress={() => Linking.openURL(ev.entryUrl!)}>
                <Text style={styles.enterBtnText}>Enter this Event →</Text>
              </TouchableOpacity>
            ) : (
              <Text style={styles.noEntry}>Online entry link isn't posted yet — check the premium list or ask the secretary.</Text>
            )}

            <Section title="Details">
              <Fact label="Date" value={fmtDate(ev.date)} />
              {ev.openingDate ? <Fact label="Entries open" value={fmtDate(ev.openingDate)} /> : null}
              {ev.closingDate ? <Fact label="Entries close" value={`${fmtDate(ev.closingDate)}${ev.closingTime ? ` · ${ev.closingTime}` : ""}`} /> : null}
              <Fact label="Location" value={[ev.address, loc, ev.zip].filter(Boolean).join(" · ")} />
              {ev.entryFee || ev.dayOfRunsFee ? <Fact label="Entry fee" value={[ev.entryFee ? `${ev.entryFee} pre-entry` : "", ev.dayOfRunsFee ? `${ev.dayOfRunsFee} day-of` : ""].filter(Boolean).join(" · ")} /> : null}
              {ev.eligibleBreeds ? <Fact label="Eligible" value={ev.eligibleBreeds} /> : null}
              {ev.eventSetting ? <Fact label="Setting" value={ev.eventSetting} /> : null}
              {ev.lureOperator ? <Fact label="Lure operator" value={ev.lureOperator} /> : null}
            </Section>

            {(ev.eventChair || ev.secretary || ev.clubWebsite) && (
              <Section title="Contacts">
                {ev.eventChair ? <Fact label="Event chair" value={[ev.eventChair, ev.eventChairContact].filter(Boolean).join(" · ")} /> : null}
                {ev.secretary || ev.secretaryContact ? <Fact label="Secretary" value={[ev.secretary, ev.secretaryContact].filter(Boolean).join(" · ")} /> : null}
                {ev.clubWebsite ? (
                  <View style={styles.factRow}>
                    <Text style={styles.factLabel}>Club site</Text>
                    <TouchableOpacity style={{ flex: 1 }} onPress={() => Linking.openURL(ev.clubWebsite!.startsWith("http") ? ev.clubWebsite! : `http://${ev.clubWebsite}`)}>
                      <Text style={styles.link}>{ev.clubWebsite!.replace(/^https?:\/\//, "")}</Text>
                    </TouchableOpacity>
                  </View>
                ) : null}
              </Section>
            )}

            {ev.eventWhatSetsApart ? (
              <Section title="About this event"><Text style={styles.aboutText}>{ev.eventWhatSetsApart}</Text></Section>
            ) : null}

            {ev.clubRaffle ? (
              <Section title="Raffle"><Text style={styles.aboutText}>{ev.clubRaffle}</Text></Section>
            ) : null}

            {gallery.length > 0 ? (
              <Section title="Photos">
                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 10 }}>
                  {gallery.slice(0, 12).map((u, i) => (
                    Platform.OS === "web"
                      // @ts-ignore
                      ? <img key={i} src={u} alt="Event photo" style={{ height: 150, borderRadius: 10, objectFit: "cover" } as any} />
                      : <Image key={i} source={{ uri: u }} style={{ height: 150, width: 200, borderRadius: 10 }} resizeMode="cover" />
                  ))}
                </ScrollView>
              </Section>
            ) : null}

            {ribbons.length > 0 ? (
              <Section title="Ribbons & rosettes">
                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 10 }}>
                  {ribbons.slice(0, 8).map((u, i) => (
                    Platform.OS === "web"
                      // @ts-ignore
                      ? <img key={i} src={u} alt="Ribbon" style={{ height: 140, borderRadius: 10, objectFit: "contain", background: "#F9FAFB" } as any} />
                      : <Image key={i} source={{ uri: u }} style={{ height: 140, width: 120, borderRadius: 10 }} resizeMode="contain" />
                  ))}
                </ScrollView>
              </Section>
            ) : null}

            {ev.eventPhotographer ? (
              <Section title="Photographer">
                {ev.eventPhotographerUrl ? (
                  <TouchableOpacity onPress={() => Linking.openURL(ev.eventPhotographerUrl!)}><Text style={styles.link}>{ev.eventPhotographer}</Text></TouchableOpacity>
                ) : <Text style={styles.aboutText}>{ev.eventPhotographer}</Text>}
              </Section>
            ) : null}

            {ev.premiumListUrl ? (
              <TouchableOpacity style={styles.premiumBtn} onPress={() => Linking.openURL(ev.premiumListUrl!)}>
                <Text style={styles.premiumBtnText}>📄 View Premium List (official details)</Text>
              </TouchableOpacity>
            ) : null}

            <View style={styles.cta}>
              <Text style={styles.ctaTitle}>Going to run?</Text>
              <Text style={styles.ctaText}>Track your dog's every run, speed, and title in the Dash-Hound app.</Text>
              <View style={{ flexDirection: "row", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
                <TouchableOpacity style={styles.ctaPrimary} onPress={() => router.push("/(auth)/what-is-fast-cat/" as any)}><Text style={styles.ctaPrimaryText}>What Is Fast CAT? →</Text></TouchableOpacity>
                <TouchableOpacity style={styles.ctaSecondary} onPress={backToEvents}><Text style={styles.ctaSecondaryText}>More Events</Text></TouchableOpacity>
              </View>
            </View>
          </View>

          <View style={styles.footer}><Text style={styles.footerText}>© 2026 Dash-Hound LLC. All rights reserved.</Text></View>
        </ScrollView>
      )}
    </View>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}
function Fact({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <View style={styles.factRow}>
      <Text style={styles.factLabel}>{label}</Text>
      <Text style={styles.factValue}>{value}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  content: { flexGrow: 1 },
  subBar: { backgroundColor: "#FFF7E6", borderBottomWidth: 1, borderBottomColor: "#F5DEB3", paddingHorizontal: 24, paddingVertical: 12 },
  subBarBack: { color: "#92400E", fontSize: 14, fontWeight: "600" },
  loading: { paddingVertical: 60, alignItems: "center" },
  hint: { color: "#6B7280", fontSize: 15 },
  hero: { backgroundColor: NAVY, paddingHorizontal: 24, paddingTop: 32, paddingBottom: 32, alignItems: "center" },
  kicker: { color: ORANGE, fontSize: 12, fontWeight: "800", letterSpacing: 1.5, marginBottom: 8 },
  title: { color: WHITE, fontSize: 28, fontWeight: "900", textAlign: "center", letterSpacing: -0.5, maxWidth: 640, lineHeight: 34 },
  club: { color: "rgba(255,255,255,0.7)", fontSize: 15, marginTop: 8, textAlign: "center" },
  body: { maxWidth: 680, width: "100%", alignSelf: "center", paddingHorizontal: 20, paddingVertical: 28 },
  section: { marginTop: 26 },
  sectionTitle: { fontSize: 13, fontWeight: "800", color: ORANGE, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 },
  factRow: { flexDirection: "row", paddingVertical: 11, borderBottomWidth: 1, borderBottomColor: "#F3F4F6", gap: 16 },
  factLabel: { width: 120, fontSize: 13, fontWeight: "700", color: "#6B7280" },
  factValue: { flex: 1, fontSize: 15, color: TEXT, lineHeight: 22 },
  link: { color: "#B45309", fontWeight: "700", fontSize: 15, textDecorationLine: "underline" },
  aboutText: { fontSize: 15, color: TEXT, lineHeight: 24 },
  enterBtn: { backgroundColor: ORANGE, paddingVertical: 16, borderRadius: 10, alignItems: "center" },
  enterBtnText: { color: NAVY, fontWeight: "900", fontSize: 17 },
  noEntry: { fontSize: 14, color: "#6B7280", fontStyle: "italic", textAlign: "center", lineHeight: 20, paddingVertical: 6 },
  premiumBtn: { marginTop: 24, paddingVertical: 14, borderRadius: 10, alignItems: "center", borderWidth: 1, borderColor: "#D1D5DB", backgroundColor: "#F9FAFB" },
  premiumBtnText: { color: NAVY, fontWeight: "700", fontSize: 15 },
  cta: { marginTop: 32, padding: 22, backgroundColor: NAVY, borderRadius: 14 },
  ctaTitle: { fontSize: 20, fontWeight: "800", color: WHITE, marginBottom: 8 },
  ctaText: { fontSize: 15, color: "rgba(255,255,255,0.75)", lineHeight: 22 },
  ctaPrimary: { backgroundColor: ORANGE, paddingHorizontal: 18, paddingVertical: 12, borderRadius: 8 },
  ctaPrimaryText: { color: NAVY, fontWeight: "800", fontSize: 14 },
  ctaSecondary: { backgroundColor: "rgba(255,255,255,0.12)", paddingHorizontal: 18, paddingVertical: 12, borderRadius: 8 },
  ctaSecondaryText: { color: WHITE, fontWeight: "800", fontSize: 14 },
  footer: { backgroundColor: NAVY_DARK, paddingVertical: 32, alignItems: "center", marginTop: 20 },
  footerText: { color: "rgba(255,255,255,0.35)", fontSize: 13 },
});
